import React, { useMemo, useState } from 'react';
import { useAppState } from '../../../client/providers/AppProvider';
import { FeatureListItem } from '../../journey/FeatureListItem';
import { PricingOption } from '../../journey/PricingOption';
import { LimitedTimeOffer } from './LimitedTimeOffer';
import { gql } from '@urql/core';
import { useAuthenticatedQuery } from '../../../client/hooks';
import { PlayerSubscriptionsQuery, PlayerSubscriptionsDocument } from '../../../generated/spin-graphql';
import { GenericInLayoutPageLoading } from '../../common/GenericFullPageLoading';
import { PlayerReferralCode } from './PlayerReferralCode';
import { MobileFeatureListItem } from '../../journey/MobileFeatureListItem';

type Props = {};

gql`
    query PlayerSubscriptions {
        espin_boolean_feature_flag(where: { name: { _eq: "players_set_as_subscribed" } }) {
            value
        }
        espin_referral_option {
            id
        }
    }
`;

export const PlayerSubscriptions: React.FC<Props> = () => {
    const [monthly, setMonthly] = useState<boolean>(false);
    const { user, subscribed } = useAppState();

    const { isReady, data } = useAuthenticatedQuery<PlayerSubscriptionsQuery>(
        {
            query: PlayerSubscriptionsDocument,
        },
        ['espin_boolean_feature_flag'],
    );

    const features: FeatureListItem[] = [
        {
            label: 'Searchable by Colleges',
            description: 'Able to be searched by subscribing colleges via game title, name, gamertag, GPA & location.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Custom Player Profile',
            description: 'Create your own custom player profile to highlight your inside & outside the game skillsets.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Add VODs & Highlights',
            description: 'Add YouTube and Twitch links of your best clips & montages for colleges to see.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'College Database',
            description:
                'Ability to see the college database and college profile pages for all colleges on the platform.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Event Registration',
            description:
                'Sign up for recruiting events with colleges watching! Events include weekly cash tournaments, recruiting showcases and LAN events with live colleges present (some events may require a ticket purchase).',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Meet with a Recruting Specialist',
            description:
                'You and your parent can talk directly with a recruiting specialist to define your first steps in your collegiate esports journey.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Featured in Recruiting Content',
            description:
                'Featured in recruitng montages, vidoes, and written articles sent directly to colleges to highlight your recent performances!',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Stay Plugged In Discord',
            description:
                'Access to the Stay Plugged In discord server where you can interact directly with Recruiting Specialists and other recruits to stay up to date on all opportunities.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Display Contact Information',
            description:
                'Ability to display your contact information for only member & approved collegs on the platform.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Social Interaction Buttons',
            description:
                'Ability to see and use the interaction buttons such as follow and message directly on the college profile page. ',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Ongoing Recruiting Meets',
            description:
                'You and your parent can talk directly with a recruiting specialist throughout your recruiting process. Our experts help answer questions, help find more colleges and keep you up to date on any opportunities.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Custom recruiting plan',
            description:
                'Develop a recruiting plan with a recruiting specialist. This plan will help you define milestones and goals to reach them throughout your recruiting journey.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Unique Content & News',
            description:
                'Continuous content that has all recruiting updates on new opportunities & new scholarship opportunities. Also included is educational material to help streamline the recruiting process.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Subscriber Only Discord',
            description:
                'Access to a subscriber only discord category where you can interact directly with Recruiting Specialists and other recruits to stay up to date on all opportunities. There will be enhanced educational material, tips & tricks, and subscriber only content.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
    ];
    const dashboardFeatures: FeatureListItem[] = [
        {
            label: 'Dashboard Features',
            description: '',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: false },
        },
        {
            label: 'Receive Notifications',
            description:
                'See any new notification in your dashboard inbox for a new profile view, follower, and new message.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Notification Reports',
            description:
                'Get continuous dashboard updates via email consisting of new profile views, followers, and new conversations.',
            free: { name: 'Free', included: true },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Profile Views',
            description: 'Ability to see the the number of notifications and WHO they came from.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Followers',
            description: 'Ability to see the the number of followers and WHO they came from.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
        {
            label: 'Messaging',
            description:
                'Ability to see the the number of messages with WHO they came from & the content of the message.',
            free: { name: 'Free', included: false },
            tier1: { name: 'Platinum', included: true },
        },
    ];

    const freeTrialActive = useMemo(() => {
        return (
            !!user?.player_profile?.trial_end_date &&
            new Date(user?.player_profile?.trial_end_date) <= new Date() &&
            user?.subscription_status?.is_subscribed
        );
    }, [user?.player_profile?.trial_end_date, user?.subscription_status?.is_subscribed]);

    const hasUsedFreeTrial = useMemo(() => {
        return !!user?.player_profile?.trial_end_date;
    }, [user?.player_profile?.trial_end_date]);

    const showReferralOptionModal = useMemo(() => {
        return !!!user?.player_profile?.referral_option_id;
    }, [user?.player_profile?.referral_option_id]);

    if (!isReady) {
        return <GenericInLayoutPageLoading />;
    }

    return (
        <div className="subscriptions">
            <div className="container">
                {(data.espin_boolean_feature_flag[0].value ||
                    !!user?.player_profile?.subscription?.referral_code_redemption_id) && <LimitedTimeOffer />}
                <div className="pricing-options">
                    <div className="title-container">
                        <h2 className="title">Simple Pricing</h2>
                        <div className="payment-intervals">
                            <button
                                onClick={() => setMonthly(true)}
                                className={`payment-interval${monthly ? ' active' : ''}`}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setMonthly(false)}
                                className={`payment-interval${!monthly ? ' active' : ''}`}
                            >
                                Yearly
                            </button>
                        </div>
                        {!(data.espin_boolean_feature_flag[0].value || subscribed) && <PlayerReferralCode />}
                    </div>
                    <div className="pricing-options-container">
                        <PricingOption
                            monthly={monthly}
                            label={'Silver'}
                            value={'Free'}
                            currentPlan={!subscribed}
                            ctaLabel={subscribed ? undefined : 'Current Plan'}
                            freeTrialOffer={false}
                            hasInterval={false}
                        />
                        <PricingOption
                            className="blue-background"
                            monthly={monthly}
                            label={'Platinum'}
                            value={monthly ? '$4' : '$49'}
                            valueCents=".99"
                            currentPlan={subscribed}
                            ctaLabel={subscribed ? 'Current Plan' : 'Upgrade'}
                            freeTrialOffer={true}
                            cardHighlightText="Most Popular"
                            showFreeTrial={!freeTrialActive && !hasUsedFreeTrial}
                            showReferralOptionModal={showReferralOptionModal && data?.espin_referral_option?.length > 0}
                        />
                    </div>
                </div>
                {subscribed && (
                    <div className="cancel-container">
                        <form action="/api/payments/create-portal-session" method="POST" className="cancel-form">
                            <input type="hidden" name="email" value={user?.email} />
                            <button className="cancel-subscription">Manage Subscription</button>
                        </form>
                    </div>
                )}
                <div className="pricing-feature-comparison">
                    <div className="feature-header">
                        <div className="header-title">Feature</div>
                        <div className="header-title">{subscribed ? 'Silver' : 'Current Plan'}</div>
                        <div className="header-title">{subscribed ? 'Current Plan' : 'Platinum'}</div>
                    </div>
                    <div className="feature-group">
                        {features.map((feature, index) => (
                            <FeatureListItem key={`feature-list-item-${index}`} feature={feature} />
                        ))}
                    </div>
                    <div className="feature-group-mobile">
                        {features.map((feature, index) => (
                            <MobileFeatureListItem key={`feature-list-item-${index}`} feature={feature} />
                        ))}
                    </div>
                    <div className="feature-group">
                        {dashboardFeatures.map((feature, index) => (
                            <FeatureListItem key={`dashboard-feature-list-item-${index}`} feature={feature} />
                        ))}
                    </div>
                    <div className="feature-group-mobile">
                        {dashboardFeatures.map((feature, index) => (
                            <MobileFeatureListItem key={`feature-list-item-${index}`} feature={feature} />
                        ))}
                    </div>
                </div>
                <div className="questions-container">
                    <h2 className="questions-title">Got Questions? Get Answers.</h2>
                    <a href="mailto:info@staypluggedin.com" className="button large-button blue-button">
                        Contact Us
                    </a>
                </div>
            </div>
        </div>
    );
};
